import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { ApplicationEventsEnum, NotifyChildEvent, NotifyParentEvent } from '../models/events';
import { StorageKeyEnum, UserAuthData } from '../models/auth';
import { getBusinessUidFromUserAuth } from '../utils/helpers';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { ModulesEnum } from '../models/modules';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class EventsService implements OnDestroy {
  private readonly ngZone = inject(NgZone);

  private childRef: BehaviorSubject<HTMLIFrameElement | null> = new BehaviorSubject<HTMLIFrameElement | null>(null);
  private childUri: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private savedUrl$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  childUrl: string = '';

  private _setAuthData$: Subject<boolean> = new Subject<boolean>();
  setAuthData$: Observable<boolean> = this._setAuthData$.asObservable();

  constructor(
    private authService: AuthService,
    private router: Router
  ) {
    window.addEventListener('message', this.incomingEventListener.bind(this));
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.incomingEventListener.bind(this));
  }

  private incomingEventListener(
    ev: MessageEvent<NotifyParentEvent>
  ): void {
    switch (ev.data.type) {
      case ApplicationEventsEnum.UserAuthData: {
        const userData = ev.data.message;
        this.authService.setUserAuthData(userData);
        localStorage.setItem(StorageKeyEnum.AuthToken, userData.AccessToken);
        this.authService.setSelectedBusiness(getBusinessUidFromUserAuth(userData));
        this.ngZone.run(() => {
          this.router.navigate([`/${ModulesEnum.Dashboard}`]);
        });
        return;
      }
      case ApplicationEventsEnum.RouteChanged: {
        window.history.replaceState(
          {},
          "",
          `${this.removeDuplicatePathSegments(`${window.location.origin}${this.cleanRouterUrl(this.router.url)}${ev.data.message ? ev.data.message : ""}`)}`
        );
        return;
      }
      case ApplicationEventsEnum.UserAuthDataRefreshed: {

        const userAuthData = ev.data.message;
        if (userAuthData) {
          const storedData = JSON.parse(localStorage.getItem(StorageKeyEnum.UserData)!) as UserAuthData;

          // set local storage here...
          this.authService.setUserAuthData({
            ...storedData,
            AccessToken: userAuthData.AccessToken,
          });
          localStorage.setItem(StorageKeyEnum.AuthToken, userAuthData.AccessToken);
          this.authService.setSelectedBusiness(getBusinessUidFromUserAuth(storedData));
        }
        return;
      }
      case ApplicationEventsEnum.Logout: {
        this.authService.logOut();
        return;
      }
      case ApplicationEventsEnum.GetAuthToken: {
        this._setAuthData$.next(true);
        return;
      }
    }
  }

  private removeDuplicatePathSegments(url: string): string {
    const segments = url.split('/');
    const cleanedUrl = Array.from(new Set(segments)).join('/');
    return cleanedUrl;
  }

  private cleanRouterUrl(url: string): string {
    const segments = url.split('/');
    const cleanedUrl = '/' + segments[1] + '/' + segments[2]
    return cleanedUrl;
  }

  /**
   * @param uri only pass in '*' if you do not know the target origin
   * @param event NotifyChildEvent is a coalesced type of events that are allowed to be passed in
   */
  notifyChild(ref: HTMLIFrameElement, event: NotifyChildEvent, uri: string): void {
    if (ref) {
      ref.contentWindow?.postMessage(event, uri);
    }
  }

  setChildUri(uri: string): void {
    this.childUri.next(uri);
  }

  get childUri$(): Observable<string | null> {
    return this.childUri.asObservable();
  }

  setChildRef(ref: HTMLIFrameElement): void {

    this.childRef.next(ref);
  }

  get childRef$(): Observable<HTMLIFrameElement | null> {
    return this.childRef.asObservable();
  }

  setSavedUrl(url: string): void {
    this.savedUrl$.next(url);
  }

  get savedUrl(): string {
    return this.savedUrl$.value;
  }
}
